import axios from "axios";

import { ENDPOINT } from "../constant/environment";
import { isEmbedMode } from "./embedMode";
import { clearSearchCurrency } from "./searchCurrency";
import { getUserTimezone } from "./userTimezone";

const STORAGE_KEY = "_zenvoya_acting_partner";

const localhostBackend = (): boolean => {
  try {
    return /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/i.test(String(ENDPOINT || ""));
  } catch {
    return false;
  }
};

const tenantHeadersEnabled = (): boolean =>
  String(process.env.REACT_APP_SEND_TENANT_HEADERS || "").toLowerCase() === "true" || localhostBackend();

export type ActingPartner = {
  partnerCode: string;
  branchCode?: string;
  channel?: string;
  name?: string;
  currency?: string;
  verticals?: string[];
  country?: string;
  source?: "portal" | "ticket";
};

const VERTICAL_ALIASES: Record<string, string> = {
  flight: "flights",
  flights: "flights",
  hotel: "hotels",
  hotels: "hotels",
  stay: "hotels",
  stays: "hotels",
  activity: "activities",
  activities: "activities",
};

const normaliseVerticals = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const out: string[] = [];
  for (const raw of value) {
    const hit = VERTICAL_ALIASES[String(raw ?? "").trim().toLowerCase()];
    if (hit && !out.includes(hit)) out.push(hit);
  }
  return out.length ? out : undefined;
};

let current: ActingPartner | null | undefined;

const readStorage = (): ActingPartner | null => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.partnerCode ? parsed : null;
  } catch {
    return null;
  }
};

export const getActingPartner = (): ActingPartner | null => {
  if (current !== undefined) return current;
  current = typeof window === "undefined" ? null : readStorage();
  return current;
};

export const isPartnerSession = (): boolean =>
  Boolean(getActingPartner()?.partnerCode);

export const setActingPartner = (value: ActingPartner | null | undefined): void => {
  if (typeof window === "undefined") return;
  if (!isEmbedMode()) return;

  const partnerCode = String(value?.partnerCode ?? "").trim();
  const next: ActingPartner | null =
    value && /^\d+$/.test(partnerCode) && partnerCode !== "0"
      ? {
          partnerCode,
          branchCode: String(value.branchCode ?? "").trim() || undefined,
          channel: String(value.channel ?? "").trim().toUpperCase() || undefined,
          name: value.name,
          currency: String(value.currency ?? "").trim().toUpperCase() || undefined,
          verticals: normaliseVerticals(value.verticals),
          country: String(value.country ?? "").trim() || undefined,
          source: value.source === "ticket" ? "ticket" : "portal",
        }
      : null;

  const previous = getActingPartner();
  if (previous?.partnerCode !== next?.partnerCode) clearSearchCurrency();

  current = next;
  try {
    if (next) window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
  }
};

export const getActingPartnerCurrency = (): string | undefined => getActingPartner()?.currency;

export const getActingPartnerCountry = (): string | undefined => getActingPartner()?.country;

const US_COUNTRY_NAMES = ["united states", "united states of america", "usa", "us"];

export const isActingPartnerInUS = (): boolean =>
  US_COUNTRY_NAMES.includes(String(getActingPartnerCountry() ?? "").trim().toLowerCase());

export const getPartnerVerticals = (): string[] | null => {
  const verticals = getActingPartner()?.verticals;
  return verticals && verticals.length ? verticals : null;
};

const isBackendUrl = (url?: string): boolean => {
  if (!url) return false;
  if (!/^https?:\/\//i.test(url)) return true;
  const base = String(ENDPOINT || "").replace(/\/+$/, "");
  if (!base) return false;
  try {
    return new URL(url).origin === new URL(base).origin;
  } catch {
    return url.startsWith(base);
  }
};

let installed = false;

export const installActingPartnerInterceptor = (): void => {
  if (installed) return;
  installed = true;

  axios.interceptors.request.use((config) => {
    if (!isBackendUrl(config.url)) return config;

    const headers: any = config.headers || (config.headers = {} as any);

    const zone = getUserTimezone();
    if (zone) headers["x-user-timezone"] = zone;

    if (!tenantHeadersEnabled()) return config;

    const acting = getActingPartner();
    headers["x-booking-channel"] = acting?.channel || "B2C";

    if (!acting) return config;

    headers["x-acting-partner-code"] = acting.partnerCode;
    if (acting.branchCode) headers["x-acting-branch-code"] = acting.branchCode;
    if (acting.channel) headers["x-acting-channel"] = acting.channel;
    return config;
  });
};
